
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import { useEffect } from '@storybook/client-api';
import contentDemo from './demo/content.twig';
import sceneDemo from './demo/scene.twig';
import extraDemo from './demo/extra.twig';
import flagImage from './demo/flag.svg';
import robotImage from './demo/robot.svg';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Cloud Cover" parameters={{
      docs: {
        inlineStories: false
      },
      layout: 'fullscreen',
      themes: {
        disable: true
      }
    }} mdxType="Meta" />
    <h1>{`Cloud Cover`}</h1>
    <p>{`A containing element for introductory page content, for example a heading and subtitle. The clouds offer a whimsical, illustrative element to help transition from the page title and `}<a parentName="p" {...{
        "href": "/docs/components-sky-nav--dark"
      }}>{`Sky Nav`}</a>{` to the main body content.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Content" height="400px" parameters={{
        docs: {
          source: {
            code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'Our Team'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We are a small, versatile team who care passionately about the web.
      We’re full of what our industry considers unicorns. Our designers
      code. Our developers went to art school.
    </p>
  {% endblock %}
{% endembed %}`
          }
        }
      }} mdxType="Story">
    {contentDemo()}
  </Story>
    </Canvas>
    <h2>{`Scenes`}</h2>
    <p>{`The cloud cover may optionally contain a "scene" containing a visual object, most likely an `}<inlineCode parentName="p">{`img`}</inlineCode>{` or `}<inlineCode parentName="p">{`svg`}</inlineCode>{` element. By default, the scene will float in the sky.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Scene" height="400px" parameters={{
        docs: {
          source: {
            code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'What We Do'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We
      {% include '@cloudfour/components/icon/icon.twig' with {
        name: 'heart',
        inline: true
      } only %}
      <span class="u-hidden-visually">love</span>
      solving tough puzzles through design and&nbsp;code.
    </p>
  {% endblock %}
  {% block scene %}
    <img class="c-cloud-cover__scene-object" src="./path/robot.svg" alt="">
  {% endblock %}
{% endembed %}`
          }
        }
      }} mdxType="Story">
    {sceneDemo({
          image: robotImage
        })}
  </Story>
    </Canvas>
    <p>{`The `}<inlineCode parentName="p">{`c-cloud-cover--horizon-scene`}</inlineCode>{` modifier applies an alternate composition ideal for scenes intended to intersect the "horizon line" (bottom) of the cloud cover.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Horizon Scene" height="400px" parameters={{
        docs: {
          source: {
            code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'What We Do'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We
      {% include '@cloudfour/components/icon/icon.twig' with {
        name: 'heart',
        inline: true
      } only %}
      <span class="u-hidden-visually">love</span>
      solving tough puzzles through design and&nbsp;code.
    </p>
  {% endblock %}
  {% block scene %}
    <img class="c-cloud-cover__scene-object" src="./path/flag.svg" alt="">
  {% endblock %}
{% endembed %}`
          }
        }
      }} mdxType="Story">
    {sceneDemo({
          class: 'c-cloud-cover--horizon-scene',
          image: flagImage
        })}
  </Story>
    </Canvas>
    <h2>{`Extra Content`}</h2>
    <p>{`You can also include supporting elements like newsletter sign-up forms, notification opt-ins or other calls to action in an adjacent cell. Note that it is not possible to show extra content `}<em parentName="p">{`and`}</em>{` a scene at the same time.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Extra Content" height="400px" parameters={{
        docs: {
          source: {
            code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'What We Do'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We
      {% include '@cloudfour/components/icon/icon.twig' with {
        name: 'heart',
        inline: true
      } only %}
      <span class="u-hidden-visually">love</span>
      solving tough puzzles through design and&nbsp;code.
    </p>
  {% endblock %}
  {% block extra %}
    <p>(Imagine a nifty newsletter signup form here 👀)</p>
  {% endblock %}
{% endembed %}`
          }
        }
      }} mdxType="Story">
    {extraDemo()}
  </Story>
    </Canvas>
    <h2>{`Full Height`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`c-cloud-cover--full-height`}</inlineCode>{` will cause the cloud cover to use all available vertical space.`}</p>
    <p>{`For this to work correctly, each parent element needs to fill its container height. For best results, use with the `}<a parentName="p" {...{
        "href": "/?path=/docs/objects-page--example"
      }}>{`Page object`}</a>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Full Height" height="600px" parameters={{
        docs: {
          source: {
            code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'What We Do'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We
      {% include '@cloudfour/components/icon/icon.twig' with {
        name: 'heart',
        inline: true
      } only %}
      <span class="u-hidden-visually">love</span>
      solving tough puzzles through design and&nbsp;code.
    </p>
  {% endblock %}
  {% block scene %}
    <img class="c-cloud-cover__scene-object" src="./path/robot.svg" alt="">
  {% endblock %}
{% endembed %}`
          }
        }
      }} mdxType="Story">
    {() => {
          useEffect(() => {
            // Set this story's `body` element to full-height
            document.body.style.height = '100%';
            // Prevent Storybook's container from affecting this layout
            document.querySelector('#root').style.display = 'contents';
          });
          return sceneDemo({
            class: 'c-cloud-cover--full-height',
            image: robotImage
          });
        }}
  </Story>
    </Canvas>
    <h2>{`Coming Soon`}</h2>
    <ul>
      <li parentName="ul">{`Graceful fallback for browsers that don't support CSS Grid Layout.`}</li>
      <li parentName="ul">{`Most likely adjustments based on actual usage in theme.`}</li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

export const content = () => (
          contentDemo()
        );
content.storyName = 'Content';
content.parameters = { storySource: { source: 'contentDemo()' }, ...{
  docs: {
    source: {
      code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'Our Team'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We are a small, versatile team who care passionately about the web.
      We’re full of what our industry considers unicorns. Our designers
      code. Our developers went to art school.
    </p>
  {% endblock %}
{% endembed %}`
    }
  }
} };

export const scene = () => (
          sceneDemo({
  image: robotImage
})
        );
scene.storyName = 'Scene';
scene.parameters = { storySource: { source: 'sceneDemo({\n  image: robotImage\n})' }, ...{
  docs: {
    source: {
      code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'What We Do'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We
      {% include '@cloudfour/components/icon/icon.twig' with {
        name: 'heart',
        inline: true
      } only %}
      <span class="u-hidden-visually">love</span>
      solving tough puzzles through design and&nbsp;code.
    </p>
  {% endblock %}
  {% block scene %}
    <img class="c-cloud-cover__scene-object" src="./path/robot.svg" alt="">
  {% endblock %}
{% endembed %}`
    }
  }
} };

export const horizonScene = () => (
          sceneDemo({
  class: 'c-cloud-cover--horizon-scene',
  image: flagImage
})
        );
horizonScene.storyName = 'Horizon Scene';
horizonScene.parameters = { storySource: { source: 'sceneDemo({\n  class: \'c-cloud-cover--horizon-scene\',\n  image: flagImage\n})' }, ...{
  docs: {
    source: {
      code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'What We Do'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We
      {% include '@cloudfour/components/icon/icon.twig' with {
        name: 'heart',
        inline: true
      } only %}
      <span class="u-hidden-visually">love</span>
      solving tough puzzles through design and&nbsp;code.
    </p>
  {% endblock %}
  {% block scene %}
    <img class="c-cloud-cover__scene-object" src="./path/flag.svg" alt="">
  {% endblock %}
{% endembed %}`
    }
  }
} };

export const extraContent = () => (
          extraDemo()
        );
extraContent.storyName = 'Extra Content';
extraContent.parameters = { storySource: { source: 'extraDemo()' }, ...{
  docs: {
    source: {
      code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'What We Do'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We
      {% include '@cloudfour/components/icon/icon.twig' with {
        name: 'heart',
        inline: true
      } only %}
      <span class="u-hidden-visually">love</span>
      solving tough puzzles through design and&nbsp;code.
    </p>
  {% endblock %}
  {% block extra %}
    <p>(Imagine a nifty newsletter signup form here 👀)</p>
  {% endblock %}
{% endembed %}`
    }
  }
} };

export const fullHeight = (() => {
  useEffect(() => {
    // Set this story's `body` element to full-height
    document.body.style.height = '100%';
    // Prevent Storybook's container from affecting this layout
    document.querySelector('#root').style.display = 'contents';
  });
  return sceneDemo({
    class: 'c-cloud-cover--full-height',
    image: robotImage
  });
});
fullHeight.storyName = 'Full Height';
fullHeight.parameters = { storySource: { source: '() => {\n  useEffect(() => {\n    // Set this story\'s `body` element to full-height\n    document.body.style.height = \'100%\';\n    // Prevent Storybook\'s container from affecting this layout\n    document.querySelector(\'#root\').style.display = \'contents\';\n  });\n  return sceneDemo({\n    class: \'c-cloud-cover--full-height\',\n    image: robotImage\n  });\n}' }, ...{
  docs: {
    source: {
      code: `{% embed '@cloudfour/components/cloud-cover/cloud-cover.twig' %}
  {% block heading %}
    {% include '@cloudfour/components/heading/heading.twig' with {
      level: -2,
      content: 'What We Do'
    } only %}
  {% endblock %}
  {% block content %}
    <p>
      We
      {% include '@cloudfour/components/icon/icon.twig' with {
        name: 'heart',
        inline: true
      } only %}
      <span class="u-hidden-visually">love</span>
      solving tough puzzles through design and&nbsp;code.
    </p>
  {% endblock %}
  {% block scene %}
    <img class="c-cloud-cover__scene-object" src="./path/robot.svg" alt="">
  {% endblock %}
{% endembed %}`
    }
  }
} };

const componentMeta = { title: 'Components/Cloud Cover', parameters: {
  docs: {
    inlineStories: false
  },
  layout: 'fullscreen',
  themes: {
    disable: true
  }
}, includeStories: ["content","scene","horizonScene","extraContent","fullHeight"],  };

const mdxStoryNameToKey = {"Content":"content","Scene":"scene","Horizon Scene":"horizonScene","Extra Content":"extraContent","Full Height":"fullHeight"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;